import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* global window */
import PropTypes from 'prop-types';
import React, { useState, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Collapse, Button } from 'reactstrap';
import { buildCabinetFormUrl } from './tvcIssuesSectionHelpers';
import TvcIssuesFixView from './tvcIssuesFixView';
const VIEWS = {
  REVIEW: 'review',
  FIX: 'fix'
};
const rowTitleFromItem = (item, listIndex) => {
  var _ref, _ref2, _ref3, _item$rowData$rowTitl, _item$rowData;
  return (_ref = (_ref2 = (_ref3 = (_item$rowData$rowTitl = (_item$rowData = item.rowData) === null || _item$rowData === void 0 ? void 0 : _item$rowData.rowTitle) !== null && _item$rowData$rowTitl !== void 0 ? _item$rowData$rowTitl : item.rowTitle) !== null && _ref3 !== void 0 ? _ref3 : item.errorDetails) !== null && _ref2 !== void 0 ? _ref2 : item.formID) !== null && _ref !== void 0 ? _ref : "Item ".concat(listIndex + 1);
};

/**
 * Single collapsible list row with Review / Fix sub-views (placeholders for now).
 */
const TvcIssuesListItem = _ref4 => {
  var _item$assetType, _item$errorType;
  let item = _ref4.item,
    listIndex = _ref4.listIndex,
    stableRowKey = _ref4.stableRowKey,
    isAnimatingOut = _ref4.isAnimatingOut,
    isOpen = _ref4.isOpen,
    onToggle = _ref4.onToggle,
    onRestoreSucceeded = _ref4.onRestoreSucceeded,
    onRowExitAnimationEnd = _ref4.onRowExitAnimationEnd;
  const _useState = useState(VIEWS.REVIEW),
    _useState2 = _slicedToArray(_useState, 2),
    viewMode = _useState2[0],
    setViewMode = _useState2[1];
  const title = rowTitleFromItem(item, listIndex);
  const cabinetFormUrl = useMemo(() => buildCabinetFormUrl(item), [item]);
  const openCabinetForm = useCallback(() => {
    if (!cabinetFormUrl || typeof window === 'undefined') return;
    window.open(cabinetFormUrl, '_blank', 'noopener,noreferrer');
  }, [cabinetFormUrl]);
  const assetLabel = String((_item$assetType = item === null || item === void 0 ? void 0 : item.assetType) !== null && _item$assetType !== void 0 ? _item$assetType : 'form').trim() || 'form';
  const handleListItemTransitionEnd = useCallback(e => {
    if (!isAnimatingOut) return;
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'opacity') return;
    onRowExitAnimationEnd();
  }, [isAnimatingOut, onRowExitAnimationEnd]);
  return /*#__PURE__*/React.createElement("li", {
    className: classNames('tvc-issues-section__list-item', {
      'tvc-issues-section__list-item--exiting': isAnimatingOut
    }),
    onTransitionEnd: handleListItemTransitionEnd
  }, /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__list-item-header",
    onClick: onToggle,
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    },
    role: "button",
    tabIndex: 0
  }, /*#__PURE__*/React.createElement("span", {
    className: "tvc-issues-section__list-item-title"
  }, title), /*#__PURE__*/React.createElement("span", {
    className: "tvc-issues-section__list-item-toggle"
  }, isOpen ? '▼' : '▶')), /*#__PURE__*/React.createElement(Collapse, {
    isOpen: isOpen
  }, /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__list-item-body"
  }, /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__item-view-toggle",
    role: "tablist"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "tvc-issues-section__item-view-btn ".concat(viewMode === VIEWS.REVIEW ? 'tvc-issues-section__item-view-btn--active' : ''),
    onClick: e => {
      e.stopPropagation();
      setViewMode(VIEWS.REVIEW);
    },
    role: "tab",
    "aria-selected": viewMode === VIEWS.REVIEW
  }, "Review"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "tvc-issues-section__item-view-btn ".concat(viewMode === VIEWS.FIX ? 'tvc-issues-section__item-view-btn--active' : ''),
    onClick: e => {
      e.stopPropagation();
      setViewMode(VIEWS.FIX);
    },
    role: "tab",
    "aria-selected": viewMode === VIEWS.FIX
  }, "Fix")), /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__item-view-panel",
    role: "tabpanel"
  }, viewMode === VIEWS.REVIEW && /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__review-panel",
    "aria-label": "Review view"
  }, item.errorDetails != null && String(item.errorDetails).trim() !== '' && String(item.errorDetails).trim() !== String((_item$errorType = item.errorType) !== null && _item$errorType !== void 0 ? _item$errorType : '').trim() && /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__review-error-details mb-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__review-error-label text-muted small"
  }, "Details"), /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__review-error-text fw-medium"
  }, item.errorDetails)), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    size: "sm",
    className: "tvc-issues-section__view-asset-btn",
    disabled: !cabinetFormUrl,
    onClick: e => {
      e.stopPropagation();
      openCabinetForm();
    }
  }, "View ".concat(assetLabel))), viewMode === VIEWS.FIX && /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__fix-panel",
    "aria-label": "Fix view"
  }, /*#__PURE__*/React.createElement(TvcIssuesFixView, {
    item: item,
    stableRowKey: stableRowKey,
    onRestoreSucceeded: onRestoreSucceeded
  }))))));
};
TvcIssuesListItem.propTypes = {
  listIndex: PropTypes.number.isRequired,
  stableRowKey: PropTypes.string.isRequired,
  isAnimatingOut: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onRestoreSucceeded: PropTypes.func.isRequired,
  onRowExitAnimationEnd: PropTypes.func.isRequired,
  item: PropTypes.shape({
    formID: PropTypes.string,
    formTypeIdentifier: PropTypes.string,
    assetType: PropTypes.string,
    rowTitle: PropTypes.string,
    rowData: PropTypes.shape({
      rowTitle: PropTypes.string
    }),
    errorDetails: PropTypes.string,
    errorType: PropTypes.string
  }).isRequired
};
export default TvcIssuesListItem;